import { createGrpcWebRequestBody, createGrpcWebRequestHeader, GrpcWebFrame, readGrpcWebResponseBody, readGrpcWebResponseHeader, readGrpcWebResponseTrailer } from "./grpc-web-format";
import { Deferred, DeferredState, mergeExtendedRpcOptions, RpcError, RpcOutputStreamController, ServerStreamingCall, UnaryCall } from "@protobuf-ts/runtime-rpc";
import { GrpcStatusCode } from "./goog-grpc-status-code";
/**
 * Implements the grpc-web protocol, supporting text format or binary
 * format on the wire. Uses the fetch API to do the HTTP requests.
 *
 * Does not support client streaming or duplex calls because grpc-web
 * does not support them.
 */
export class GrpcWebFetchTransport {
    constructor(defaultOptions) {
        this.defaultOptions = defaultOptions;
    }
    mergeOptions(options) {
        return mergeExtendedRpcOptions(this.defaultOptions, options);
    }
    /**
     * Create an URI for a gRPC web call.
     *
     * Takes the `baseUrl` option and appends:
     * - slash "/"
     * - package name
     * - dot "."
     * - service name
     * - slash "/"
     * - method name
     *
     * If the service was declared without a package, the package name and dot
     * are omitted.
     *
     * All names are used exactly like declared in .proto.
     */
    makeUrl(method, options) {
        let base = options.baseUrl;
        if (base.endsWith('/'))
            base = base.substring(0, base.length - 1);
        return `${base}/${method.service.typeName}/${method.name}`;
    }
    clientStreaming( /*method: MethodInfo<I, O>, options: RpcOptions*/) {
        throw new RpcError('Client streaming is not supported by grpc-web', GrpcStatusCode[GrpcStatusCode.UNIMPLEMENTED]);
    }
    duplex( /*method: MethodInfo<I, O>, options: RpcOptions*/) {
        throw new RpcError('Duplex streaming is not supported by grpc-web', GrpcStatusCode[GrpcStatusCode.UNIMPLEMENTED]);
    }
    serverStreaming(method, input, options) {
        var _a, _b;
        let opt = options, format = (_a = opt.format) !== null && _a !== void 0 ? _a : 'text', url = this.makeUrl(method, opt), inputBytes = method.I.toBinary(input, opt.binaryOptions), defHeader = new Deferred(), responseStream = new RpcOutputStreamController(), maybeStatus, defStatus = new Deferred(), maybeTrailer, defTrailer = new Deferred();
        globalThis.fetch(url, {
            method: 'POST',
            headers: createGrpcWebRequestHeader(new globalThis.Headers(), format, opt.deadline, opt.meta),
            body: createGrpcWebRequestBody(inputBytes, format),
            signal: options.abort
        })
            .then(fetchResponse => {
            let [code, detail, meta] = readGrpcWebResponseHeader(fetchResponse);
            defHeader.resolve(meta);
            if (code !== GrpcStatusCode.OK)
                throw new RpcError(detail !== null && detail !== void 0 ? detail : GrpcStatusCode[code], GrpcStatusCode[code], meta);
            return fetchResponse.body;
        }, reason => {
            // failed to fetch, aborted, wrong url or network problem
            if (reason instanceof Error && reason.name === 'AbortError')
                throw new RpcError(reason.message, GrpcStatusCode[GrpcStatusCode.CANCELLED]);
            throw new RpcError(reason instanceof Error ? reason.message : reason);
        })
            .then(responseBody => {
            return readGrpcWebResponseBody(responseBody, format, (type, data) => {
                switch (type) {
                    case GrpcWebFrame.DATA:
                        responseStream.notifyMessage(method.O.fromBinary(data, opt.binaryOptions));
                        break;
                    case GrpcWebFrame.TRAILER:
                        let code, detail;
                        [code, detail, maybeTrailer] = readGrpcWebResponseTrailer(data);
                        maybeStatus = {
                            code: GrpcStatusCode[code],
                            detail: detail !== null && detail !== void 0 ? detail : GrpcStatusCode[code]
                        };
                        break;
                }
            });
        })
            .then(() => {
            if (!maybeTrailer)
                throw new RpcError(`missing trailers`, GrpcStatusCode[GrpcStatusCode.DATA_LOSS]);
            if (!maybeStatus)
                throw new RpcError(`missing status`, GrpcStatusCode[GrpcStatusCode.INTERNAL]);
            if (maybeStatus.code !== 'OK')
                throw new RpcError(maybeStatus.detail, maybeStatus.code, maybeTrailer);
            responseStream.notifyComplete();
            defStatus.resolve(maybeStatus);
            defTrailer.resolve(maybeTrailer);
        })
            .catch(reason => {
            // RpcErrors are thrown by us, everything else is an internal error
            let error = reason instanceof RpcError ? reason
                : new RpcError(reason instanceof Error ? reason.message : reason, GrpcStatusCode[GrpcStatusCode.INTERNAL]);
            defHeader.rejectPending(error);
            responseStream.notifyError(error);
            defStatus.rejectPending(error);
            defTrailer.rejectPending(error);
        });
        return new ServerStreamingCall(method, (_b = opt.meta) !== null && _b !== void 0 ? _b : {}, input, defHeader.promise, responseStream, defStatus.promise, defTrailer.promise);
    }
    unary(method, input, options) {
        var _a, _b;
        let opt = options, format = (_a = opt.format) !== null && _a !== void 0 ? _a : 'text', url = this.makeUrl(method, opt), inputBytes = method.I.toBinary(input, opt.binaryOptions), defHeader = new Deferred(), maybeMessage, defMessage = new Deferred(), maybeStatus, defStatus = new Deferred(), maybeTrailer, defTrailer = new Deferred();
        globalThis.fetch(url, {
            method: 'POST',
            headers: createGrpcWebRequestHeader(new globalThis.Headers(), format, opt.deadline, opt.meta),
            body: createGrpcWebRequestBody(inputBytes, format),
            signal: options.abort
        })
            .then(fetchResponse => {
            let [statusCode, statusDetail, responseMeta] = readGrpcWebResponseHeader(fetchResponse);
            defHeader.resolve(responseMeta);
            if (statusCode !== GrpcStatusCode.OK)
                throw new RpcError(statusDetail !== null && statusDetail !== void 0 ? statusDetail : GrpcStatusCode[statusCode], GrpcStatusCode[statusCode], responseMeta);
            return fetchResponse.body;
        }, reason => {
            // failed to fetch, aborted, wrong url or network problem
            if (reason instanceof Error && reason.name === 'AbortError')
                throw new RpcError(reason.message, GrpcStatusCode[GrpcStatusCode.CANCELLED]);
            throw new RpcError(reason instanceof Error ? reason.message : reason);
        })
            .then(responseBody => {
            return readGrpcWebResponseBody(responseBody, format, (type, data) => {
                switch (type) {
                    case GrpcWebFrame.DATA:
                        if (defMessage.state === DeferredState.RESOLVED)
                            throw new RpcError(`unary call received 2nd message`, GrpcStatusCode[GrpcStatusCode.DATA_LOSS]);
                        maybeMessage = method.O.fromBinary(data, opt.binaryOptions);
                        break;
                    case GrpcWebFrame.TRAILER:
                        let code, detail;
                        [code, detail, maybeTrailer] = readGrpcWebResponseTrailer(data);
                        maybeStatus = {
                            code: GrpcStatusCode[code],
                            detail: detail !== null && detail !== void 0 ? detail : GrpcStatusCode[code]
                        };
                        break;
                }
            });
        })
            .then(() => {
            if (!maybeTrailer)
                throw new RpcError(`missing trailers`, GrpcStatusCode[GrpcStatusCode.DATA_LOSS]);
            if (!maybeStatus)
                throw new RpcError(`missing status`, GrpcStatusCode[GrpcStatusCode.INTERNAL]);
            if (!maybeMessage && maybeStatus.code === 'OK')
                throw new RpcError('expected error status', GrpcStatusCode[GrpcStatusCode.DATA_LOSS]);
            if (!maybeMessage)
                throw new RpcError(maybeStatus.detail, maybeStatus.code, maybeTrailer);
            defMessage.resolve(maybeMessage);
            if (maybeStatus.code !== 'OK')
                throw new RpcError(maybeStatus.detail, maybeStatus.code, maybeTrailer);
            defStatus.resolve(maybeStatus);
            defTrailer.resolve(maybeTrailer);
        })
            .catch(reason => {
            // RpcErrors are thrown by us, everything else is an internal error
            let error = reason instanceof RpcError ? reason
                : new RpcError(reason instanceof Error ? reason.message : reason, GrpcStatusCode[GrpcStatusCode.INTERNAL]);
            defHeader.rejectPending(error);
            defMessage.rejectPending(error);
            defStatus.rejectPending(error);
            defTrailer.rejectPending(error);
        });
        return new UnaryCall(method, (_b = opt.meta) !== null && _b !== void 0 ? _b : {}, input, defHeader.promise, defMessage.promise, defStatus.promise, defTrailer.promise);
    }
}
//# sourceMappingURL=grpc-web-transport.js.map